import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Card Component
 *
 * A versatile card container with clean styling.
 * Uses standard Tailwind classes for maximum compatibility.
 */

export function Card({
  children,
  variant = 'default',
  hoverable = false,
  selected = false,
  className,
  ...props
}) {
  const variants = {
    default: [
      'bg-white',
      'border border-gray-200',
    ],
    elevated: [
      'bg-white',
      'border-0',
      'shadow-lg',
    ],
    outlined: [
      'bg-transparent',
      'border-2 border-gray-300',
    ],
    ghost: [
      'bg-gray-50',
      'border border-transparent',
    ],
    glass: [
      'bg-white/70',
      'backdrop-blur-xl',
      'border border-white/40',
    ],
  };

  return (
    <div
      className={cn(
        // Base styles
        'relative',
        'rounded-xl',
        'overflow-hidden',
        'z-0',
        'transition-all duration-200 ease-out',

        // Variant
        variants[variant],

        // Default shadow for default variant
        variant === 'default' && 'shadow-sm',

        // Hoverable state
        hoverable && [
          'cursor-pointer',
          'hover:shadow-lg',
          'hover:border-gray-300',
          'hover:-translate-y-1',
          'hover:z-20',
          'active:translate-y-0',
          'active:shadow-sm',
        ],
        // Keep card above siblings while interacting with dropdowns
        'focus-within:z-20',

        // Selected state with ring
        selected && [
          'border-blue-500',
          'ring-4 ring-blue-100',
          'shadow-lg',
        ],

        className
      )}
      {...props}
    >
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

/**
 * Card Header
 */
export function CardHeader({
  children,
  title,
  subtitle,
  action,
  className,
  ...props
}) {
  // If children provided, render them directly
  if (children) {
    return (
      <div
        className={cn(
          'px-6 py-5',
          'border-b border-gray-100',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Otherwise render structured header
  return (
    <div
      className={cn(
        'px-6 py-5',
        'border-b border-gray-100',
        'flex items-center justify-between gap-4',
        className
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-sm text-gray-500 truncate mt-1">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

/**
 * Card Content
 */
export function CardContent({
  children,
  padding = 'default',
  className,
  ...props
}) {
  const paddings = {
    none: 'p-0',
    sm: 'p-4',
    default: 'p-6',
    lg: 'p-8',
  };

  return (
    <div className={cn(paddings[padding], className)} {...props}>
      {children}
    </div>
  );
}

/**
 * Card Footer
 */
export function CardFooter({
  children,
  align = 'right',
  className,
  ...props
}) {
  const alignments = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div
      className={cn(
        'px-6 py-4',
        'border-t border-gray-100',
        'bg-gray-50',
        'flex items-center gap-3',
        alignments[align],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Card Section - For dividing content areas
 */
export function CardSection({
  children,
  title,
  className,
  ...props
}) {
  return (
    <div
      className={cn(
        'px-6 py-5',
        'border-t border-gray-100',
        'bg-gray-50/50',
        className
      )}
      {...props}
    >
      {title && (
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
          {title}
        </h4>
      )}
      {children}
    </div>
  );
}

/**
 * Empty State Card
 */
export function EmptyCard({
  icon: Icon,
  title,
  description,
  action,
  className,
  ...props
}) {
  return (
    <Card className={cn('text-center', className)} {...props}>
      <CardContent padding="lg" className="py-16">
        {Icon && (
          <div className={cn(
            'mx-auto w-16 h-16 rounded-2xl',
            'bg-gray-100',
            'flex items-center justify-center mb-6',
          )}>
            <Icon className="w-8 h-8 text-gray-400" />
          </div>
        )}
        {title && (
          <h3 className="text-xl font-semibold text-gray-900 mb-3 tracking-tight">
            {title}
          </h3>
        )}
        {description && (
          <p className="text-gray-500 max-w-md mx-auto mb-8 leading-relaxed">
            {description}
          </p>
        )}
        {action}
      </CardContent>
    </Card>
  );
}

export default Card;
